import { useState } from 'react'
import { Clock, CheckCircle, Plus } from 'lucide-react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { formatCloserLeadDate, getCanalIcon } from '@/components/CloserLeads/Modals/closerLeadDetailsUtils'
import type { CloserLead } from '@/types'

interface CloserLeadDetailsHistoryTabProps {
  lead: CloserLead
  newPendingText: string
  setNewPendingText: (v: string) => void
  onAddPending: () => void
  onTogglePending?: (pendingId: number) => void
  onRemovePending?: (pendingId: number) => void
}

export function CloserLeadDetailsHistoryTab({
  lead,
  newPendingText,
  setNewPendingText,
  onAddPending,
  onTogglePending,
  onRemovePending,
}: CloserLeadDetailsHistoryTabProps) {
  const [historySubTab, setHistorySubTab] = useState<'timeline' | 'tasks'>('timeline')
  const timeline = lead.timeline || []
  const pendencias = lead.pendencias || []
  const openTasks = pendencias.filter(p => !p.resolved).length

  return (
    <motion.div key='history' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className='flex-1 overflow-y-auto'>
      <div className='glass-card overflow-hidden flex flex-col min-h-[400px]'>
        <div className='p-2 m-4 mb-0 rounded-xl bg-white/[0.02] border border-white/[0.06] flex gap-1'>
          <button
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
              historySubTab === 'timeline' ? 'bg-accent text-white shadow-lg' : 'text-text-muted hover:text-text-primary'
            }`}
            onClick={() => setHistorySubTab('timeline')}
          >
            <Clock size={12} /> Timeline
          </button>
          <button
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all relative ${
              historySubTab === 'tasks' ? 'bg-accent text-white shadow-lg' : 'text-text-muted hover:text-text-primary'
            }`}
            onClick={() => setHistorySubTab('tasks')}
          >
            <CheckCircle size={12} /> Tasks
            {openTasks > 0 && (
              <span className={`absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-[9px] rounded-full font-bold border-2 border-[var(--bg-card)] ${
                historySubTab === 'tasks' ? 'bg-white text-accent' : 'bg-red-500 text-white'
              }`}>
                {openTasks}
              </span>
            )}
          </button>
        </div>

        <div className='flex-1 overflow-y-auto p-4'>
          {historySubTab === 'timeline' ? (
            <div className='space-y-4 relative pl-3'>
              <div className='absolute left-5 top-4 bottom-4 w-px bg-gradient-to-b from-accent/50 via-white/10 to-transparent' />
              {timeline.length === 0 ? (
                <div className='flex flex-col items-center justify-center py-20 text-text-muted/30'>
                  <Clock size={48} strokeWidth={1} />
                  <p className='text-[10px] font-bold uppercase tracking-widest mt-4'>Sem Registros</p>
                </div>
              ) : (
                [...timeline].reverse().map((h, i) => (
                  <div key={i} className='relative pl-8 group'>
                    <div className='absolute left-[-2px] top-1.5 w-3 h-3 rounded-full bg-[var(--bg-card)] border-2 border-accent z-10' />
                    <div className='bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 group-hover:bg-white/[0.06] transition-all'>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-[9px] font-bold text-accent uppercase tracking-wider'>
                          Tentativa #{h.tentativa || (timeline.length - i)}
                        </span>
                        <span className='text-[9px] text-text-muted font-bold'>{formatCloserLeadDate(h.data)}</span>
                      </div>
                      <div className='flex items-center gap-2 mb-2'>
                        <div className='p-1.5 rounded-lg bg-accent/10 text-accent'>{getCanalIcon(h.canal)}</div>
                        <span className='text-[10px] font-bold px-2 py-1 rounded-lg bg-white/5 text-text-muted uppercase tracking-wider'>{h.resultado}</span>
                      </div>
                      {h.obs && <p className='text-[11px] text-text-primary font-medium leading-relaxed'>{h.obs}</p>}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className='space-y-4'>
              <div className='glass-card p-4 space-y-3'>
                <input
                  className='w-full bg-white/5 p-3 rounded-xl text-sm text-text-primary font-bold outline-none placeholder:text-text-muted/40 border border-white/[0.06] hover:bg-white/10 transition-all'
                  placeholder='O QUE PRECISA SER FEITO?'
                  value={newPendingText}
                  onChange={e => setNewPendingText(e.target.value)}
                />
                <Button variant='primary' size='sm' icon={<Plus size={14} />} className='w-full' onClick={onAddPending} disabled={!newPendingText.trim()}>
                  Adicionar
                </Button>
              </div>
              {pendencias.map(p => (
                <div key={p.id} className='glass-card p-3 flex items-center gap-3'>
                  <button
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      p.resolved ? 'bg-accent border-accent' : 'border-accent/30'
                    }`}
                    onClick={() => onTogglePending?.(p.id)}
                  >
                    {p.resolved && <CheckCircle size={12} className='text-white' />}
                  </button>
                  <span className={`flex-1 text-[11px] font-bold ${p.resolved ? 'line-through opacity-50' : ''}`}>{p.texto}</span>
                  <button onClick={() => onRemovePending?.(p.id)} className='text-text-muted hover:text-red-400 transition-colors'>&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
